import { useEffect, useRef } from "react";
// player.js has no first-party types; the runtime API is { Player(iframe) }
// with on(event, cb), getCurrentTime(cb), getDuration(cb).
import playerjs from "player.js";

type Props = {
  src: string;
  resumeAt: number;
  onProgress: (positionSec: number, durationSec: number) => void;
  /** Every player tick, unthrottled: feeds the watch map (which parts were played). */
  onTick?: (positionSec: number, durationSec: number) => void;
  throttleMs?: number;
};

export function BunnyPlayer({ src, resumeAt, onProgress, onTick, throttleMs = 30_000 }: Props) {
  const tickRef = useRef(onTick);
  tickRef.current = onTick; // latest callback without re-subscribing the player
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastWriteRef = useRef<number>(0);
  const durationRef = useRef<number>(0);
  const seededRef = useRef<boolean>(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const player = new playerjs.Player(iframe);

    player.on("ready", () => {
      player.getDuration((d: number) => {
        durationRef.current = d || 0;
      });
      if (resumeAt > 0 && !seededRef.current) {
        seededRef.current = true;
        try {
          player.setCurrentTime(resumeAt);
        } catch {
          // setCurrentTime not always supported pre-play; ignore
        }
      }
    });

    player.on("timeupdate", (e: { seconds: number; duration: number }) => {
      const positionSec = e?.seconds ?? 0;
      const durationSec = e?.duration ?? durationRef.current ?? 0;
      tickRef.current?.(positionSec, durationSec);
      const now = Date.now();
      if (now - lastWriteRef.current < throttleMs) return;
      lastWriteRef.current = now;
      onProgress(positionSec, durationSec);
    });

    player.on("ended", () => {
      const duration = durationRef.current;
      onProgress(duration, duration);
    });

    return () => {
      try {
        player.off("timeupdate");
        player.off("ended");
        player.off("ready");
      } catch {
        // adapter may already be torn down
      }
    };
  }, [src, resumeAt, onProgress, throttleMs]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      loading="lazy"
      className="bunny-iframe"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />
  );
}
